import { useTranslation } from "react-i18next"

/**
 * The 3 ordered step keys carried by the wizard. `welcome` is NOT a stepper
 * dot — it's a post-completion confirmation page; its progress bar is hidden
 * (`required_steps.length === 0` on the welcome page or the page renders no
 * <WizardProgress>).
 *
 * Server emits `required_steps` with values from this enum; FE narrows on
 * the literal type so a backend drift (e.g. `"shipping"`) fails the build.
 */
export type WizardStepKey = "tiktok" | "charter" | "address"

export const WIZARD_STEP_KEYS: readonly WizardStepKey[] = [
  "tiktok",
  "charter",
  "address",
] as const

export interface WizardProgressProps {
  /**
   * Server-computed required steps for the active brand's path. Length 3 on
   * the first-brand happy path (`tiktok | charter | address`); length 2 on
   * the second-brand path (TikTok skipped server-side).
   */
  required_steps: readonly WizardStepKey[]
  /**
   * Current step key. Position in `required_steps` drives the
   * `aria-current="step"` flag + `data-active` styling.
   */
  current_step: WizardStepKey
  className?: string
}

/**
 * WizardProgress — semantic stepper for the OKS-24 multi-brand onboarding
 * wizard.
 *
 * Renders an `<ol>` of step dots; each `<li>`:
 *   - carries `data-step="<key>"` so Playwright + Vitest can address the
 *     dot semantically (regardless of position) — TRD v2 contract.
 *   - carries `data-testid="wizard-progress-${i}"` (1-indexed) for cases
 *     that need positional addressability.
 *   - sets `aria-current="step"` on the active dot per WAI-ARIA stepper
 *     pattern (UX Spec §7).
 *
 * The visible "Step X of N" label is rendered inside an `<nav>` above the
 * dot row; screen readers announce it via the parent `aria-label`. Inactive
 * dots carry `aria-hidden="true"` to avoid double-announcement; the active
 * dot stays in the AT tree so its `aria-current="step"` is heard (per
 * WAI-ARIA stepper pattern + UX Spec §7).
 *
 * Returns `null` when `required_steps.length === 0` — used by the Welcome
 * page (post-completion). Returns `null` when `current_step` isn't in
 * `required_steps` (defensive — server should never emit that combo).
 */
export function WizardProgress({
  required_steps,
  current_step,
  className,
}: WizardProgressProps) {
  const { t } = useTranslation()
  if (required_steps.length === 0) return null

  const currentIndex = required_steps.indexOf(current_step)
  if (currentIndex < 0) return null

  // 1-indexed for human-facing copy; matches the testid suffix.
  const stepNumber = currentIndex + 1
  const total = required_steps.length

  return (
    <nav
      data-testid="wizard-progress"
      aria-label={t("wizard.progress.aria_label", {
        step: stepNumber,
        total,
      })}
      className={["flex flex-col items-center gap-2", className ?? ""].join(
        " ",
      )}
    >
      <span
        className="text-muted-foreground text-sm font-medium tabular-nums"
        data-testid="wizard-progress-label"
      >
        {t("wizard.progress.label", { step: stepNumber, total })}
      </span>
      <ol className="flex items-center gap-1.5">
        {required_steps.map((step, index) => {
          const isActive = index === currentIndex
          const isCompleted = index < currentIndex
          return (
            <li
              key={step}
              data-step={step}
              data-testid={`wizard-progress-${index + 1}`}
              data-active={isActive ? "true" : undefined}
              data-completed={isCompleted ? "true" : undefined}
              aria-current={isActive ? "step" : undefined}
              // Inactive dots are decorative — the canonical AT signal is
              // the parent `<nav aria-label="Step X of N">`. Active dot
              // stays exposed so `aria-current="step"` reaches AT.
              aria-hidden={isActive ? undefined : "true"}
              className={
                isActive
                  ? "bg-foreground h-1.5 w-6 rounded-full transition-all"
                  : isCompleted
                    ? "bg-foreground/60 h-1.5 w-3 rounded-full transition-all"
                    : "bg-foreground/20 h-1.5 w-3 rounded-full transition-all"
              }
            />
          )
        })}
      </ol>
    </nav>
  )
}

export default WizardProgress
